import { initializeApp } from 'firebase/app';
import {
  GoogleAuthProvider,
  getAuth,
  getRedirectResult,
  onAuthStateChanged,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  type User,
} from 'firebase/auth';

const cfg = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID,
};

export const app = initializeApp(cfg);
export const auth = getAuth(app);

export function watchAuth(cb: (u: User | null) => void) {
  return onAuthStateChanged(auth, cb);
}

/**
 * Popup first, redirect as the fallback. Mobile browsers block or mangle popups often
 * enough that popup-only sign-in strands users on the login screen with no visible reason.
 */
export async function signIn(): Promise<void> {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });
  try {
    await signInWithPopup(auth, provider);
  } catch (err) {
    const code = (err as { code?: string })?.code ?? '';
    if (
      code === 'auth/popup-blocked' ||
      code === 'auth/cancelled-popup-request' ||
      code === 'auth/operation-not-supported-in-this-environment'
    ) {
      await signInWithRedirect(auth, provider);
      return;
    }
    throw err;
  }
}

/** Resolves a redirect-based sign-in when the page comes back. Safe to call unconditionally. */
export function completeRedirectSignIn() {
  return getRedirectResult(auth).catch(() => null);
}

export function signOutUser() {
  return signOut(auth);
}

/**
 * The Edge Functions verify this token and resolve the caller's org from its `uid` (see
 * supabase/functions/_shared/org.ts) — no custom claims are involved, so nothing here has
 * to wait on a token refresh to pick up a new org. Refreshes automatically when expired.
 */
export async function getIdToken(): Promise<string> {
  const u = auth.currentUser;
  if (!u) throw new Error('not signed in');
  return u.getIdToken();
}
